import { useEffect, useMemo, useRef, useState } from 'react';
import type { GeoJSONSource, MapMouseEvent } from 'mapbox-gl';
import { UMMap, UMMapControls, type UMMapHandle } from '../../urbanmap/_components';
import { NJText } from '@engie-group/fluid-design-system-react';
import { boundsOf } from './domain';
import { buildingsFC, drawFC, networksFC, perimeterLayers, ringFC, stationLayers, stationsFC } from './mapLayers';
import type { Commune, DrawShape, HeatNetwork, LngLat, PlanBuilding } from './types';
import { MapSearchBar } from './MapSearchBar';

/** Station affichée sur la carte : point de mesure + zone d'influence. */
export interface StationMarker { id: string; name: string; position: LngLat; area: LngLat[] }

interface Props {
  buildings: PlanBuilding[];
  included: Set<string>;
  outlines: LngLat[][];
  /** Réseaux existants repris par le projet. */
  networks: HeatNetwork[];
  /** Recentrage ponctuel (recherche de lieu) : `id` change à chaque demande. */
  focus?: { id: string; shape: LngLat[] } | null;
  /** Outil de sélection armé : le cliquer-glisser dessine un polygone. */
  drawing: boolean;
  shape: DrawShape;
  points: LngLat[];
  closed: boolean;
  /** Polygone terminé au relâchement de la souris. */
  onPolygon: (points: LngLat[]) => void;
  /** Stations météo affichées dès qu'un périmètre est renseigné. */
  stations?: StationMarker[];
  selectedStation?: string | null;
  /** Clic sur un point ou une zone de station. */
  onStationPick?: (id: string) => void;
  /** Position suivie à l'écran (fiche station ancrée au point). */
  anchor?: LngLat | null;
  /** Rendu de l'ancre : reçoit les coordonnées pixel dans le conteneur de carte. */
  children?: (pos: { x: number; y: number }) => React.ReactNode;
  /** Barre de recherche UrbanMap en haut à gauche. */
  search?: { communes: Commune[]; communeOutlines: Record<string, LngLat[]> };
}

/** Déplacement minimum, en pixels, pour retenir un sommet à main levée ou valider une emprise. */
const STEP_PX = 6;
const MIN_PX = 14;

const rectangle = (a: LngLat, b: LngLat): LngLat[] => [a, [b[0], a[1]], b, [a[0], b[1]]];

/** Carte de l'assistant : bâtiments UM du référentiel, inclus/exclus, réseaux et sélection d'une zone. */
export function PerimeterMap({ buildings, included, outlines, networks, focus, drawing, shape, points, closed, onPolygon, stations = [], selectedStation = null, onStationPick, anchor = null, children, search }: Props) {
  const mapRef = useRef<UMMapHandle>(null);
  const [mapMode, setMapMode] = useState<'mapbox' | 'offline'>('mapbox');
  const [basemapError, setBasemapError] = useState(false);
  const [anchorPx, setAnchorPx] = useState<{ x: number; y: number } | null>(null);
  const latest = useRef({ drawing, shape, onPolygon, onStationPick });
  latest.current = { drawing, shape, onPolygon, onStationPick };

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return undefined;
    const failed = (event: { error: Error; sourceId?: string }) => {
      if (event.sourceId === 'composite') setBasemapError(true);
    };
    map.on('error', failed);
    return () => { map.off('error', failed); };
  }, []);

  const layers = useMemo(() => [...perimeterLayers(), ...stationLayers()], []);
  const sources = useMemo(() => ({
    buildings: buildingsFC(buildings, (b) => ({ inc: included.has(b.id) ? 1 : 0 })),
    outline: ringFC(outlines),
    networks: networksFC(networks),
    draw: drawFC(points, closed),
    stations: stationsFC(stations, selectedStation),
  }), [buildings, included, outlines, networks, points, closed, stations, selectedStation]);

  /** Chaque commune ou réseau ajouté recadre la carte sur le nouveau périmètre. */
  const focusKey = useMemo(
    () => [outlines.length, ...networks.map((n) => n.id)].join('|'),
    [outlines.length, networks],
  );
  useEffect(() => {
    const shapes = [...outlines, ...networks.map((n) => n.path)].flat();
    if (shapes.length === 0) return;
    mapRef.current?.fitBounds(boundsOf(shapes));
    // Recadrage uniquement quand la composition du périmètre change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey]);

  /** Recherche de lieu : on vole vers la commune demandée. */
  const focusId = focus?.id ?? null;
  useEffect(() => {
    if (!focus || focus.shape.length === 0) return;
    mapRef.current?.fitBounds(boundsOf(focus.shape));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId]);

  /** Dessin au cliquer-glisser : aperçu imperatif du polygone, validation au relâchement. */
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return undefined;
    const preview = (pts: LngLat[]) => (map.getSource('draw') as GeoJSONSource | undefined)?.setData(drawFC(pts, false));

    let drag: { start: LngLat; startPx: { x: number; y: number }; path: LngLat[] } | null = null;

    const current = (e: MapMouseEvent): LngLat => [e.lngLat.lng, e.lngLat.lat];
    const down = (e: MapMouseEvent) => {
      if (!latest.current.drawing) return;
      e.preventDefault();
      drag = { start: current(e), startPx: { x: e.point.x, y: e.point.y }, path: [current(e)] };
    };
    const move = (e: MapMouseEvent) => {
      if (!drag) return;
      if (latest.current.shape === 'rectangle') {
        preview(rectangle(drag.start, current(e)));
        return;
      }
      const last = map.project(drag.path[drag.path.length - 1]);
      if (Math.hypot(last.x - e.point.x, last.y - e.point.y) < STEP_PX) return;
      drag.path.push(current(e));
      if (drag.path.length >= 3) preview(drag.path);
    };
    const up = (e: MapMouseEvent) => {
      if (!drag) return;
      const far = Math.hypot(drag.startPx.x - e.point.x, drag.startPx.y - e.point.y) >= MIN_PX;
      const pts = latest.current.shape === 'rectangle' ? rectangle(drag.start, current(e)) : drag.path;
      drag = null;
      if (far && pts.length >= 3) latest.current.onPolygon(pts);
      else preview([]);
    };

    map.on('mousedown', down);
    map.on('mousemove', move);
    map.on('mouseup', up);
    return () => {
      map.off('mousedown', down);
      map.off('mousemove', move);
      map.off('mouseup', up);
    };
  }, []);

  /** Pendant le dessin, le glissé trace la zone au lieu de déplacer la carte. */
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    if (drawing) map.dragPan.disable();
    else map.dragPan.enable();
  }, [drawing]);

  /** Clic sur une station : point d'abord, puis zone d'influence. */
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return undefined;
    const click = (e: MapMouseEvent) => {
      const pick = latest.current.onStationPick;
      if (!pick || latest.current.drawing) return;
      const ids = ['pc-station-point', 'pc-station-zone'].filter((l) => map.getLayer(l));
      if (ids.length === 0) return;
      const hit = map.queryRenderedFeatures(e.point, { layers: ids })[0];
      const id = hit?.properties?.id;
      if (typeof id === 'string') pick(id);
    };
    map.on('click', click);
    return () => { map.off('click', click); };
  }, []);

  /** Survol des stations : curseur pointeur. */
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !onStationPick) return undefined;
    /** La carte peut être détruite ou pas encore initialisée : le canvas n'est alors pas disponible. */
    const canvas = () => { try { return map.getCanvas(); } catch { return undefined; } };
    const move = (e: MapMouseEvent) => {
      const ids = ['pc-station-point', 'pc-station-zone'].filter((l) => map.getLayer(l));
      if (ids.length === 0) return;
      const over = map.queryRenderedFeatures(e.point, { layers: ids }).length > 0;
      const c = canvas();
      if (c) c.style.cursor = over ? 'pointer' : '';
    };
    map.on('mousemove', move);
    return () => {
      map.off('mousemove', move);
      const c = canvas();
      if (c) c.style.cursor = '';
    };
  }, [onStationPick]);

  /** Suivi de l'ancre : la fiche station reste collée à son point pendant les déplacements. */
  const anchorKey = anchor ? `${anchor[0]},${anchor[1]}` : '';
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !anchor) { setAnchorPx(null); return undefined; }
    const sync = () => {
      const p = map.project(anchor);
      setAnchorPx({ x: p.x, y: p.y });
    };
    sync();
    map.on('move', sync);
    map.on('resize', sync);
    return () => { map.off('move', sync); map.off('resize', sync); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorKey]);

  return (
    <div className={drawing ? 'pc-drawing' : undefined} style={{ position: 'relative', width: '100%', height: '100%' }}>
      <style>{'.pc-drawing .mapboxgl-canvas-container, .pc-drawing .mapboxgl-canvas{cursor:crosshair!important}'}</style>
      <UMMap ref={mapRef} sources={sources} layers={layers} onModeChange={setMapMode} />
      {(mapMode === 'offline' || basemapError) && (
        <NJText as="span" scale="xs" className="pcv3-map-mode" role="status">
          {basemapError ? 'Fond Mapbox indisponible · vérifiez le token et l’accès réseau.' : 'Fond hors ligne · token Mapbox non configuré'}
        </NJText>
      )}
      <UMMapControls basemap="light" availableBasemaps={['light']} onZoomIn={() => mapRef.current?.zoomIn()} onZoomOut={() => mapRef.current?.zoomOut()} />
      {search && <MapSearchBar mapRef={mapRef} communes={search.communes} communeOutlines={search.communeOutlines}
        home={outlines.length ? outlines.flat() : points} />}
      {anchorPx && children?.(anchorPx)}
    </div>
  );
}
